import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AuthPageShell } from '@/features/auth/components/auth-page-shell'
import { normalizeReturnUrl } from '@/features/auth/guards'
import { loginWithPassword } from '@/features/auth/services/auth-service'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'

type LoginSearch = {
  redirect?: string
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  component: LoginRoute,
})

function LoginRoute() {
  const navigate = useNavigate()
  const { redirect } = Route.useSearch()
  const setSession = useAuthSessionStore((state) => state.setSession)
  const setInitialized = useAuthSessionStore((state) => state.setInitialized)
  const isAuthenticated = useAuthSessionStore((state) => state.isAuthenticated)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const target = normalizeReturnUrl(redirect)

  if (isAuthenticated()) {
    void navigate({ to: target })
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!email.trim() || !password) {
      toast.error('请输入邮箱和密码')
      return
    }

    setIsSubmitting(true)

    try {
      const session = await loginWithPassword({
        email: email.trim(),
        password,
      })
      setSession(session)
      setInitialized(true)
      toast.success('登录成功')
      await navigate({ to: target })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '登录失败，请稍后重试')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthPageShell
      title="登录快剪"
      description="如果你已经在主站有账号，这里直接复用同一套认证接口。登录后会继续跳回你刚才访问的页面。"
      footerText="还没有账号？"
      footerActionLabel="立即注册"
      footerActionTo="/register"
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="space-y-2">
          <Label htmlFor="email">邮箱</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">密码</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            placeholder="请输入密码"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
          {isSubmitting ? '登录中...' : '登录并进入工作台'}
        </Button>
      </form>
    </AuthPageShell>
  )
}